
import React, { useState } from "react";
import { RibaStage, Project, Approval } from "@/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProject } from "@/contexts/ProjectContext";
import { toast } from "@/hooks/use-toast";
import { AlertTriangle, Check, Clock, Plus, UserCheck, X } from "lucide-react";

interface StageApprovalsProps {
  project: Project;
  stage: RibaStage;
  onUpdate?: () => void;
}

export default function StageApprovals({ project, stage, onUpdate }: StageApprovalsProps) {
  const { addApproval } = useProject();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newApproval, setNewApproval] = useState<Partial<Approval>>({
    name: "",
    description: "",
    approver: "",
    status: "pending",
    dateRequested: new Date(),
  });
  
  const handleAddApproval = () => {
    if (!newApproval.name || !newApproval.description || !newApproval.approver) {
      toast({
        title: "Missing information",
        description: "Please provide a name, description, and approver",
        variant: "destructive",
      });
      return;
    }
    
    const approval: Approval = {
      id: `app-${Date.now().toString(36)}`,
      name: newApproval.name || "",
      description: newApproval.description || "",
      approver: newApproval.approver || "",
      status: "pending",
      dateRequested: new Date(),
      comments: newApproval.comments,
    };
    
    // In a real implementation, this would add to the project context
    addApproval(project.id, stage.id, approval);
    
    toast({
      title: "Approval requested",
      description: `Approval request has been sent to ${approval.approver}`
    });
    
    // Reset form
    setNewApproval({
      name: "",
      description: "",
      approver: "",
      status: "pending",
      dateRequested: new Date(),
    });
    
    setIsDialogOpen(false);
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <Check className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };
  
  const getStatusText = (status: string) => {
    switch (status) {
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      default:
        return "Pending";
    }
  };
  
  const getStatusClass = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-amber-100 text-amber-800";
    }
  };
  
  // Check if all required approvals are completed for this stage
  const isStageApproved = stage.approvals.length > 0 && 
    stage.approvals.every(approval => approval.status === "approved");
  
  // Determine if next stage can be unlocked
  const canUnlockNextStage = isStageApproved && 
    stage.status === "in-progress" && 
    project.currentStage === stage.id;
  
  // Determine if approval form should be available
  const showApprovalForm = stage.status !== "completed";
  
  const handleUnlockNextStage = () => {
    // This would update project context in a real implementation
    toast({
      title: "Stage complete",
      description: `Stage ${stage.id}: ${stage.name} has been completed. Moving to next stage.`
    });
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Approvals</CardTitle>
            <CardDescription>Stage gateway approvals</CardDescription>
          </div>
          {showApprovalForm && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Request Approval
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Approval</DialogTitle>
                  <DialogDescription>
                    Request formal approval for this stage or deliverable.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Approval Name</label>
                    <Input 
                      placeholder="E.g., Stage Gateway Approval"
                      value={newApproval.name}
                      onChange={(e) => setNewApproval({...newApproval, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Description</label>
                    <Textarea 
                      placeholder="Describe what needs to be approved..."
                      value={newApproval.description}
                      onChange={(e) => setNewApproval({...newApproval, description: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Approver</label>
                    <Input 
                      placeholder="Name of the approver"
                      value={newApproval.approver}
                      onChange={(e) => setNewApproval({...newApproval, approver: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Additional Comments</label>
                    <Textarea 
                      placeholder="Any additional comments for the approver..."
                      value={newApproval.comments}
                      onChange={(e) => setNewApproval({...newApproval, comments: e.target.value})}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleAddApproval}>Submit Request</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {canUnlockNextStage && (
            <div className="bg-green-50 border-green-200 border rounded-md p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Check className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">All approvals complete</h3>
                  <div className="mt-2">
                    <p className="text-sm text-green-700">
                      This stage has received all required approvals and is ready to be completed.
                    </p>
                  </div>
                  <div className="mt-4">
                    <Button onClick={handleUnlockNextStage}>
                      Complete Stage and Move to Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {stage.approvals && stage.approvals.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Approver</TableHead>
                  <TableHead>Date Requested</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stage.approvals.map((approval) => (
                  <TableRow key={approval.id} className="cursor-pointer hover:bg-muted">
                    <TableCell className="font-medium">
                      <div>
                        {approval.name}
                        {approval.description && (
                          <p className="text-xs text-muted-foreground mt-1">{approval.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{approval.approver}</TableCell>
                    <TableCell>{approval.dateRequested.toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(approval.status)}`}>
                        {getStatusIcon(approval.status)}
                        <span className="ml-1">
                          {getStatusText(approval.status)}
                        </span>
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <UserCheck className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No approvals requested for this stage</p>
              {showApprovalForm && (
                <Button className="mt-4" variant="outline" onClick={() => setIsDialogOpen(true)}>
                  Request Approval
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
